// Caravan - caravans, guards, and wave spawning

import { Vec2, CONST, clamp } from './utils.js';
import { randFn } from './rng.js';
import { t } from './i18n.js';
import { flipSprite, whitePalette } from './renderer.js';
import {
  PIXEL_SCALE, fxClock, quantiseDir, makeGrid, stepLine, rasterLocal,
  GLOW_SMALL, GLOW_LARGE, GLOW_FIRE, GLOW_STICKY,
  ARROW_PALETTE, arrowSprite, FLAME_PALETTE, FLAME_FRAMES,
} from './pixel-art.js';

// Caravan types
export const CaravanType = {
  DONKEY: 'donkey',
  WAGON: 'wagon',
  ROYAL: 'royal',
};

const CARAVAN_DEFS = {
  [CaravanType.DONKEY]: {
    speed: CONST.DONKEY_SPEED,
    radius: 14,
    lootMin: 10,
    lootMax: 25,
    guardCount: 1,
    hp: 30,
    get label() { return t('caravan.donkey'); },
  },
  [CaravanType.WAGON]: {
    speed: CONST.WAGON_SPEED,
    radius: 18,
    lootMin: 30,
    lootMax: 60,
    guardCount: 2,
    hp: 60,
    get label() { return t('caravan.wagon'); },
  },
  [CaravanType.ROYAL]: {
    speed: CONST.ROYAL_SPEED,
    radius: 22,
    lootMin: 80,
    lootMax: 150,
    guardCount: 3,
    hp: 100,
    get label() { return t('caravan.royal'); },
  },
};

// Guard types
export const GuardType = {
  BASIC: 'basic',
  ARMORED: 'armored',
  ARCHER: 'archer',
  // v1.6.0 late-wave archetypes. Both exist to pressure builds that
  // trivialised the game by holding dash: the lancer forces the player to
  // *time* the dodge (telegraphed charge), the bombardier ignores i-frames
  // entirely because its blast is a ground field, not a hit.
  LANCER: 'lancer',
  BOMBARDIER: 'bombardier',
};

// Guards that hold distance and attack from range rather than closing in.
// Reads the archetype table (see GUARD_DEFS) so it can't drift from it.
export function isRangedGuard(type) {
  return !!guardDef(type).ranged;
}

// Guard AI states
export const GuardState = {
  PATROL: 'patrol',
  CHASE: 'chase',
  RETURN: 'return',
};

// Lancer charge phases. Sequence is windup → dash → recover → (null).
const ChargePhase = {
  WINDUP: 'windup',
  DASH: 'dash',
  RECOVER: 'recover',
};

// --- Elite tiers ---------------------------------------------------------
//
// Difficulty used to be delivered purely by head-count, which is exactly
// what killed the frame rate late game (600+ guards by wave 70) while
// adding almost no pressure — a marching column that long never reaches
// the player. planWaveGuards caps the head count and spends the surplus
// here instead: the same wave budget arrives as fewer, much nastier
// bodies. Index = tier; 0 is a plain guard.
export const EliteTier = {
  NONE: 0,
  VETERAN: 1,
  CHAMPION: 2,
  EXECUTIONER: 3,
};

export const ELITE_TIERS = [
  null,
  { id: 'veteran', hp: 1.9, dmg: 1.30, armor: 1.4, speed: 1.05, mark: '#e08a3c' },
  { id: 'champion', hp: 3.4, dmg: 1.70, armor: 1.9, speed: 1.12, mark: '#e74c3c' },
  { id: 'executioner', hp: 6.0, dmg: 2.20, armor: 2.5, speed: 1.20, mark: '#ffd700' },
];

// Armour/cloth recolours that make a tier readable at a glance without
// needing a separate sprite per tier. Keys match the shared sprite palette
// letters ('A'/'a' = body, 'H'/'h' = head).
const ELITE_TINTS = [
  null,
  { A: '#a9743f', a: '#84582f' },
  { A: '#a8322f', a: '#7d2422' },
  { A: '#2b2b33', a: '#1a1a20', H: '#c9a227', h: '#9a7c1d' },
];

const _elitePaletteCache = new Map();

// Palette for (base palette × elite tier). Cached by base-palette identity
// so the renderer's raster cache sees a stable object and rasterises each
// combination exactly once.
function elitePalette(base, tier) {
  if (!tier || !ELITE_TINTS[tier]) return base;
  let byTier = _elitePaletteCache.get(base);
  if (!byTier) {
    byTier = [];
    _elitePaletteCache.set(base, byTier);
  }
  if (!byTier[tier]) {
    byTier[tier] = { ...base, ...ELITE_TINTS[tier] };
  }
  return byTier[tier];
}

// Pixel-art sprites

const GUARD_PALETTE = {
  'H': '#34495e', // helmet
  'h': '#2c3e50', // helmet dark
  'F': '#d4a574', // face
  'E': '#1a1a1a', // eyes
  'A': '#7f8c8d', // armor
  'a': '#636e72', // armor dark
  'P': '#4a4a5a', // pants
  'p': '#3a3a4a', // pants dark
  'S': '#5a4a3a', // shoes
  'W': '#bdc3c7', // weapon (sword)
};

const GUARD_SPRITE = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAA ',
  ' aAAAa ',
  ' .AAA. ',
  ' .PPP. ',
  ' pP.Pp ',
  ' SS.SS ',
];

const GUARD_WALK_1 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  'WAAAAA ',
  ' aAAAa ',
  ' .AAA. ',
  ' .PPP. ',
  ' pP..P ',
  ' SS..S ',
];

const GUARD_WALK_2 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAW',
  ' aAAAa ',
  ' .AAA. ',
  ' .PPP. ',
  ' P..Pp ',
  ' S..SS ',
];

// Armored guard sprites
const ARMORED_PALETTE = {
  'H': '#5a5a6a', // heavy helmet
  'h': '#4a4a5a', // helmet dark
  'F': '#d4a574', // face
  'E': '#1a1a1a', // eyes
  'A': '#6a6a7a', // heavy armor
  'a': '#5a5a6a', // armor dark
  'P': '#4a4a5a', // pants
  'p': '#3a3a4a', // pants dark
  'S': '#5a4a3a', // shoes
  'W': '#8a8a9a', // shield
};

const ARMORED_SPRITE = [
  ' .HHH. ',
  ' hhHhh ',
  ' .FEF. ',
  'WAAAAAW',
  'WaAAAaW',
  ' .AAA. ',
  ' .PPP. ',
  ' pP.Pp ',
  ' SS.SS ',
];

const ARMORED_WALK_1 = [
  ' .HHH. ',
  ' hhHhh ',
  ' .FEF. ',
  'WAAAAAW',
  'WaAAAaW',
  ' .AAA. ',
  ' .PPP. ',
  ' pP..P ',
  ' SS..S ',
];

const ARMORED_WALK_2 = [
  ' .HHH. ',
  ' hhHhh ',
  ' .FEF. ',
  'WAAAAAW',
  'WaAAAaW',
  ' .AAA. ',
  ' .PPP. ',
  ' P..Pp ',
  ' S..SS ',
];

// Archer guard sprites
const ARCHER_PALETTE = {
  'H': '#2e4a1e', // hood
  'h': '#1e3a0e', // hood dark
  'F': '#d4a574', // face
  'E': '#1a1a1a', // eyes
  'A': '#4a6a3a', // tunic
  'a': '#3a5a2a', // tunic dark
  'P': '#5a4a3a', // pants
  'p': '#4a3a2a', // pants dark
  'S': '#5a4a3a', // shoes
  'B': '#6a4a2a', // bow
};

const ARCHER_SPRITE = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAA ',
  ' aAAAaB',
  ' .AAA.B',
  ' .PPP. ',
  ' pP.Pp ',
  ' SS.SS ',
];

const ARCHER_WALK_1 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAA ',
  ' aAAAaB',
  ' .AAA.B',
  ' .PPP. ',
  ' pP..P ',
  ' SS..S ',
];

const ARCHER_WALK_2 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAA ',
  ' aAAAaB',
  ' .AAA.B',
  ' .PPP. ',
  ' P..Pp ',
  ' S..SS ',
];

// Lancer sprites. Read as "archer silhouette with a spear": the body is
// pixel art, the spear itself is drawn in render() along `facing` (see
// renderLancerSpear) — a pixel row could only point left or right, and the
// spear is what the charge telegraph points with.
const LANCER_PALETTE = {
  'H': '#3a2e5a', // helmet
  'h': '#2a2044', // helmet dark
  'F': '#d4a574', // face
  'E': '#1a1a1a', // eyes
  'A': '#5a4a7a', // gambeson
  'a': '#4a3a63', // gambeson dark
  'P': '#4a4a5a', // pants
  'p': '#3a3a4a', // pants dark
  'S': '#5a4a3a', // shoes
  'G': '#d4a574', // gripping hand
};

const LANCER_SPRITE = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAG',
  ' aAAAa ',
  ' .AAA. ',
  ' .PPP. ',
  ' pP.Pp ',
  ' SS.SS ',
];

const LANCER_WALK_1 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAG',
  ' aAAAa ',
  ' .AAA. ',
  ' .PPP. ',
  ' pP..P ',
  ' SS..S ',
];

const LANCER_WALK_2 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAG',
  ' aAAAa ',
  ' .AAA. ',
  ' .PPP. ',
  ' P..Pp ',
  ' S..SS ',
];

// Bombardier sprites. Squat, dark, and carrying a lit charge ('O' with a
// bright 'W' fuse) so the thrower is identifiable before the bomb lands.
const BOMBARDIER_PALETTE = {
  'H': '#4a3520', // cap
  'h': '#3a2718', // cap dark
  'F': '#d4a574', // face
  'E': '#1a1a1a', // eyes
  'A': '#6a5230', // leather coat
  'a': '#54401f', // coat dark
  'P': '#4a4a3a', // pants
  'p': '#3a3a2a', // pants dark
  'S': '#5a4a3a', // shoes
  'O': '#2a2a2a', // bomb
  'W': '#ffb347', // lit fuse
};

const BOMBARDIER_SPRITE = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAW',
  ' aAAAaO',
  ' .AAA. ',
  ' .PPP. ',
  ' pP.Pp ',
  ' SS.SS ',
];

const BOMBARDIER_WALK_1 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAW',
  ' aAAAaO',
  ' .AAA. ',
  ' .PPP. ',
  ' pP..P ',
  ' SS..S ',
];

const BOMBARDIER_WALK_2 = [
  ' .HHH. ',
  ' hhhhh ',
  ' .FEF. ',
  ' AAAAAW',
  ' aAAAaO',
  ' .AAA. ',
  ' .PPP. ',
  ' P..Pp ',
  ' S..SS ',
];

// Caravan sprites - viewed from the side
const DONKEY_PALETTE = {
  'B': '#8B7355', // body
  'b': '#6B5335', // body dark
  'H': '#7B6345', // head
  'E': '#1a1a1a', // eye
  'L': '#5a4a3a', // legs
  'T': '#4a3a2a', // tail
  'S': '#a08060', // saddle/pack
  's': '#806040', // saddle dark
};

const DONKEY_SPRITE = [
  ' ..SS.. ',
  ' .SssS. ',
  'H.BBBB.',
  'HE.BBB.',
  '..bBBb..',
  '..L..L..',
  '..L..L..',
];

const WAGON_PALETTE = {
  'W': '#8B6914', // wood
  'w': '#6B4914', // wood dark
  'C': '#a08050', // canvas top
  'c': '#806030', // canvas dark
  'R': '#4a3a2a', // wheels
  'r': '#3a2a1a', // wheel dark
  'A': '#5a4a3a', // axle
};

const WAGON_SPRITE = [
  ' .CCC. ',
  ' cCCCc ',
  ' CCCCC ',
  ' WWWWW ',
  ' wWWWw ',
  'R.AAA.R',
  'rR...Rr',
];

const ROYAL_PALETTE = {
  'G': '#ffd700', // gold trim
  'R': '#8b0000', // royal red
  'r': '#6b0000', // royal dark
  'W': '#f0e0c0', // white
  'w': '#d0c0a0', // white dark
  'D': '#4a3a2a', // wheels
  'd': '#3a2a1a', // wheel dark
  'A': '#5a4a3a', // axle
  'C': '#c0a040', // crown ornament
};

const ROYAL_SPRITE = [
  ' ..C.. ',
  ' .GGG. ',
  ' GRRG. ',
  ' RRRRR ',
  ' rRWRr ',
  ' WWWWW ',
  'D.AAA.D',
  'dD...Dd',
];


// --- Guard archetypes ----------------------------------------------------
//
// Everything type-specific about a guard lives in one row here: stats,
// sprites, HP-bar colour, and the two behaviours that actually differ
// between archetypes — how the guard positions itself in CHASE (`ranged` +
// `preferredDist`, or `charges` for the lunge) and what an attack produces.
//
// Before this, each new archetype meant editing two switch statements in
// the constructor plus a handful of scattered `type === GuardType.X` tests
// in update / canAttack / attack. Adding one is now a single entry, and
// the shared code reads flags instead of enumerating types. Matches how
// the rest of the codebase describes variants (CARAVAN_DEFS, CARDS,
// RELICS, ELITE_TIERS).

// Attack builders. Each takes the guard and the player's current state and
// returns the payload the caller turns into damage / a projectile / a bomb.
function meleeAttack(guard) {
  return { ranged: false, damage: guard.damage };
}

export const ArrowVariant = {
  PLAIN: 'plain',
  FIRE: 'fire',
  STICKY: 'sticky',
};

// Odds of each arrow variant at a given wave: { sticky, fire }, the rest is
// plain. Sticky and fire are mutually exclusive — an archer fires one kind
// of special arrow or a plain one, never a hybrid.
export function arrowVariantChances(wave) {
  let sticky = 0;
  if (wave >= CONST.STICKY_ARROW_MIN_WAVE) {
    sticky = Math.min(
      CONST.STICKY_ARROW_CHANCE_CAP,
      CONST.STICKY_ARROW_CHANCE_BASE
        + (wave - CONST.STICKY_ARROW_MIN_WAVE) * CONST.STICKY_ARROW_CHANCE_PER_WAVE
    );
  }
  // Fire-arrow chance ramps with wave past FIRE_ARROW_MIN_WAVE.
  // Keeps regular arrows in the mix forever (cap < 1) so the player
  // doesn't see only fire after wave 10. Fire claims its share of what
  // the sticky roll left rather than the whole range — otherwise, once
  // both chances are high, their union covers everything and plain
  // arrows disappear, which is exactly what that cap exists to prevent.
  let fire = 0;
  if (wave >= CONST.FIRE_ARROW_MIN_WAVE) {
    fire = Math.min(0.85, 0.4 + (wave - CONST.FIRE_ARROW_MIN_WAVE) * 0.05) * (1 - sticky);
  }
  return { sticky, fire };
}

// Pick the variant of an archer's next arrow. Rolled ahead of the shot and
// kept in `guard.nextArrow` so the arrow on the bow shows what will fly.
// One rand() partitions the whole decision, so every roll costs exactly one
// draw and seeded reruns keep a fixed-length RNG stream.
export function rollArrowVariant(guard) {
  const roll = randFn(guard._rng)();
  const { sticky, fire } = arrowVariantChances(guard.wave);
  if (roll < sticky) return ArrowVariant.STICKY;
  if (roll < sticky + fire) return ArrowVariant.FIRE;
  return ArrowVariant.PLAIN;
}

function archerAttack(guard, playerPos, playerVel) {
  const rand = randFn(guard._rng);
  // Lead the target — aim where the player will be after the arrow
  // travels there. Imperfect (factor 0.65) so the player can still
  // dodge by changing direction. Without lead, archers always shot
  // where the player WAS and were trivial to kite.
  const distNow = playerPos.sub(guard.pos).len();
  const travelTime = distNow / CONST.PROJECTILE_SPEED;
  const leadFactor = 0.65;
  const lead = playerVel
    ? new Vec2(playerVel.x * travelTime * leadFactor, playerVel.y * travelTime * leadFactor)
    : new Vec2(0, 0);
  // A guard brain may pick the aim point (order.arrowAim); the spread, the
  // arrow variant and the flip-back check below stay the same.
  let target = (guard.order && guard.order.arrowAim) || playerPos.add(lead);
  // If the lead pushed the target past the archer (happens when the
  // player dashes inward — PLAYER_DASH_SPEED * leadFactor > PROJECTILE_SPEED),
  // drop the lead and aim at the player's current position so the
  // shot doesn't fire in the opposite direction.
  if (target.sub(guard.pos).dot(playerPos.sub(guard.pos)) <= 0) {
    target = playerPos;
  }
  let dir = target.sub(guard.pos).normalize();
  // Random aim spread (±~6°). Mostly hits, occasionally misses —
  // keeps archer fire from feeling sniper-precise.
  const spread = (rand() - 0.5) * 0.21;
  const cs = Math.cos(spread);
  const sn = Math.sin(spread);
  dir = new Vec2(dir.x * cs - dir.y * sn, dir.x * sn + dir.y * cs);

  // The variant was rolled after the previous shot (see rollArrowVariant)
  // so the archer could show it on the bow; a guard that shoots before its
  // first update rolls now. Then the next one is rolled straight away.
  const variant = guard.nextArrow ?? rollArrowVariant(guard);
  guard.nextArrow = rollArrowVariant(guard);
  const sticky = variant === ArrowVariant.STICKY;
  const fire = variant === ArrowVariant.FIRE;
  if (fire) guard.hasFiredFire = true;
  // Sticky arrows trade damage for the movement lockdown.
  const damage = sticky
    ? Math.max(1, Math.round(guard.damage * CONST.STICKY_DAMAGE_MUL))
    : guard.damage;
  return { ranged: true, damage, origin: guard.pos.copy(), dir, fire, sticky };
}

// Lob a fused charge at where the player is heading. The caller turns this
// into a Bomb; the blast resolves on the fuse, not here.
function bombardierAttack(guard, playerPos, playerVel) {
  const rand = randFn(guard._rng);
  const lead = playerVel
    ? new Vec2(
      playerVel.x * CONST.BOMB_FUSE_SEC * CONST.BOMB_LEAD_FACTOR,
      playerVel.y * CONST.BOMB_FUSE_SEC * CONST.BOMB_LEAD_FACTOR
    )
    : new Vec2(0, 0);
  const scatter = new Vec2(
    (rand() - 0.5) * 2 * CONST.BOMB_SCATTER,
    (rand() - 0.5) * 2 * CONST.BOMB_SCATTER
  );
  // A guard brain may pick the aim point (order.bombAim); the scatter and
  // everything else about the throw stay the same.
  const aim = (guard.order && guard.order.bombAim) || playerPos.add(lead);
  return {
    ranged: false,
    bomb: true,
    damage: guard.damage,
    origin: guard.pos.copy(),
    target: aim.add(scatter),
  };
}

const GUARD_DEFS = {
  [GuardType.BASIC]: {
    hp: CONST.GUARD_BASE_HP,
    damage: CONST.GUARD_BASE_DAMAGE,
    speed: CONST.GUARD_SPEED,
    detection: CONST.GUARD_DETECTION_RANGE,
    attackRange: CONST.GUARD_ATTACK_RANGE,
    attackCooldown: CONST.GUARD_ATTACK_COOLDOWN,
    armor: 0,
    idle: GUARD_SPRITE,
    walk: [GUARD_WALK_1, GUARD_WALK_2],
    palette: GUARD_PALETTE,
    barColor: '#e67e22',
    attack: meleeAttack,
  },
  [GuardType.ARMORED]: {
    hp: CONST.ARMORED_GUARD_HP,
    damage: CONST.ARMORED_GUARD_DAMAGE,
    speed: CONST.ARMORED_GUARD_SPEED,
    detection: CONST.ARMORED_GUARD_DETECTION_RANGE,
    attackRange: CONST.GUARD_ATTACK_RANGE,
    attackCooldown: CONST.GUARD_ATTACK_COOLDOWN,
    armor: CONST.ARMORED_GUARD_ARMOR,
    idle: ARMORED_SPRITE,
    walk: [ARMORED_WALK_1, ARMORED_WALK_2],
    palette: ARMORED_PALETTE,
    barColor: '#3498db',
    attack: meleeAttack,
  },
  [GuardType.ARCHER]: {
    hp: CONST.ARCHER_GUARD_HP,
    damage: CONST.ARCHER_GUARD_DAMAGE,
    speed: CONST.ARCHER_GUARD_SPEED,
    detection: CONST.ARCHER_GUARD_DETECTION_RANGE,
    attackRange: CONST.ARCHER_GUARD_ATTACK_RANGE,
    attackCooldown: CONST.ARCHER_GUARD_ATTACK_COOLDOWN,
    armor: 0,
    idle: ARCHER_SPRITE,
    walk: [ARCHER_WALK_1, ARCHER_WALK_2],
    palette: ARCHER_PALETTE,
    barColor: '#2ecc71',
    ranged: true,
    preferredDist: CONST.ARCHER_PREFERRED_DIST,
    attack: archerAttack,
  },
  [GuardType.LANCER]: {
    hp: CONST.LANCER_HP,
    damage: CONST.LANCER_DAMAGE,
    speed: CONST.LANCER_SPEED,
    detection: CONST.LANCER_DETECTION_RANGE,
    attackRange: CONST.LANCER_ATTACK_RANGE,
    attackCooldown: CONST.LANCER_ATTACK_COOLDOWN,
    armor: 0,
    idle: LANCER_SPRITE,
    walk: [LANCER_WALK_1, LANCER_WALK_2],
    palette: LANCER_PALETTE,
    barColor: '#9b59b6',
    charges: true,
    attack: meleeAttack,
  },
  [GuardType.BOMBARDIER]: {
    hp: CONST.BOMBARDIER_HP,
    damage: CONST.BOMBARDIER_DAMAGE,
    speed: CONST.BOMBARDIER_SPEED,
    detection: CONST.BOMBARDIER_DETECTION_RANGE,
    attackRange: CONST.BOMBARDIER_ATTACK_RANGE,
    attackCooldown: CONST.BOMBARDIER_ATTACK_COOLDOWN,
    armor: 0,
    idle: BOMBARDIER_SPRITE,
    walk: [BOMBARDIER_WALK_1, BOMBARDIER_WALK_2],
    palette: BOMBARDIER_PALETTE,
    barColor: '#e8a33d',
    ranged: true,
    preferredDist: CONST.BOMBARDIER_PREFERRED_DIST,
    attack: bombardierAttack,
  },
};

// Unknown types fall back to BASIC rather than throwing — a stale saved
// type or a typo degrades to a plain guard instead of breaking the wave.
export function guardDef(type) {
  return GUARD_DEFS[type] || GUARD_DEFS[GuardType.BASIC];
}

// --- Weapon overlays ------------------------------------------------------
//
// The spear and the nocked arrow point along `facing`, which a mirrored
// pixel row can't do — so they are pixel art generated per direction (see
// pixel-art.js): the facing is quantised to DIRECTIONS (32) steps with
// hysteresis (the last index is kept on the guard as `_fxDir`), shapes are
// rasterised on the cell grid at that exact angle, and the result is an
// ordinary sprite aligned to the guard sprite's own grid. Effects (glow,
// flame, drip, glint) are small fixed sprites or single cells on the same
// grid. Render must never touch the guard's RNG.

const GUARD_SCALE = PIXEL_SCALE;
// Guard sprites are 7×9 cells; the overlay grid is centred on cell (3, 4).
const GUARD_COLS = 7;
const GUARD_ROWS = 9;
const OVERLAY_SIZE = 41; // odd, so its centre lands on a cell of the guard grid

// Top-left screen px of the guard sprite — the same rounding pixelSprite
// uses, so overlay cells sit exactly on the sprite's pixels.
function gridOrigin(guard) {
  return {
    ox: Math.round(guard.pos.x - (GUARD_COLS * GUARD_SCALE) / 2),
    oy: Math.round(guard.pos.y - (GUARD_ROWS * GUARD_SCALE) / 2),
  };
}

// One cell of the guard grid (may lie outside the 7×9 body).
function cell(renderer, g, c, r, color) {
  renderer.rect(g.ox + c * GUARD_SCALE, g.oy + r * GUARD_SCALE, GUARD_SCALE, GUARD_SCALE, color);
}

// An odd-sized sprite centred on guard-grid cell (c, r).
function spriteAt(renderer, g, c, r, sprite, palette) {
  renderer.pixelSprite(
    g.ox + c * GUARD_SCALE + GUARD_SCALE / 2,
    g.oy + r * GUARD_SCALE + GUARD_SCALE / 2,
    sprite, palette, GUARD_SCALE
  );
}

// --- Spear ---

// Shaft is dark wood so it reads on sand; the steel tip is near-white with
// a dark rim; the red pennant marks the business end from afar.
const SPEAR_PALETTE = {
  'L': '#7a5230', // shaft
  'K': '#1e2230', // blade rim
  'W': '#ffffff', // blade, lit edge
  'T': '#dfe6ec', // blade
  'S': '#9aa6b4', // blade, shaded edge
  'R': '#d83a3a', // pennant
  'r': '#8e1f1f', // pennant dark
};
const TELEGRAPH = '#ff4d4d';

const BLADE_LEN = 5;
const _spearCache = new Map();

// Upper normal of a direction: the perpendicular pointing up-screen, where
// a pennant flies. A vertical spear flies it to the left.
function upperNormal(fx, fy) {
  if (Math.abs(fx) < 1e-6) return [-1, 0];
  return fx > 0 ? [fy, -fx] : [-fy, fx];
}

// Spear sprite centred on the gripping hand, at the exact angle of `dir`.
// Thrust (cells the spear sits forward of its rest position): pulled back
// over the windup, thrown out through the lunge. Returns { rows, tip } with
// the tip in cells from the hand.
//
//   shaft   — stepped line from the butt (3 cells behind the hand) to the
//             socket
//   blade   — a leaf polygon rasterised by cell centres plus its stepped
//             centre line, so no angle breaks it into loose pixels; lit
//             where the cell above is open, shaded where the one below is,
//             rimmed underneath (light from the top at any angle)
//   pennant — a small triangle flying off the upper side behind the socket
export function spearSprite(dir, thrust) {
  const key = `${dir.i}|${thrust}`;
  let sprite = _spearCache.get(key);
  if (sprite) return sprite;
  const grid = makeGrid(OVERLAY_SIZE);
  const { fx, fy } = dir;
  const len = 9 + thrust;
  const [nx, ny] = upperNormal(fx, fy);
  const shaft = stepLine(0, 0, fx, fy, -3 + thrust, len);
  for (const [c, r] of shaft) grid.put(c, r, 'L');

  // Pennant first, only on open cells, so shaft and blade stay on top.
  const flag = rasterLocal(fx * len, fy * len, fx, fy, nx, ny, 5, (u, w) =>
    u >= -3.2 && u <= 0 && w >= 0.5 && w <= 0.5 + 2.2 * (1 + u / 3.2));
  for (const [c, r] of flag) {
    if (grid.get(c, r) !== ' ') continue;
    const u = (c - fx * len) * fx + (r - fy * len) * fy;
    grid.put(c, r, u < -1.8 ? 'r' : 'R');
  }

  // Blade: base just past the shaft end, widest 1.5 cells in, to the point.
  const bc = fx * (len + 0.5);
  const br = fy * (len + 0.5);
  const blade = rasterLocal(bc, br, fx, fy, nx, ny, BLADE_LEN + 2, (u, w) => {
    if (u < 0 || u > BLADE_LEN) return false;
    const half = u < 1.5 ? 1.35 : 1.35 * (BLADE_LEN - u) / (BLADE_LEN - 1.5);
    return Math.abs(w) <= half + 0.2;
  });
  const spine = stepLine(bc, br, fx, fy, 0, BLADE_LEN);
  const cells = new Map();
  for (const [c, r] of [...blade, ...spine]) cells.set(`${c},${r}`, [c, r]);
  const has = (c, r) => cells.has(`${c},${r}`);
  for (const [c, r] of cells.values()) {
    grid.put(c, r, !has(c, r - 1) ? 'W' : !has(c, r + 1) ? 'S' : 'T');
  }
  const tip = spine[spine.length - 1];
  grid.put(tip[0], tip[1], 'W');
  for (const [c, r] of cells.values()) {
    if (grid.get(c, r + 1) === ' ') grid.put(c, r + 1, 'K');
  }
  const socket = shaft[shaft.length - 1];
  if (!has(socket[0], socket[1])) grid.put(socket[0], socket[1], 'K');

  sprite = { rows: grid.done(), tip };
  _spearCache.set(key, sprite);
  return sprite;
}

// Glint on the spear tip while winding up: a plus that grows.
const GLINT_PALETTE = { 'W': '#ffffff', 'R': TELEGRAPH };
const GLINT_SMALL = [
  ' W ',
  'WRW',
  ' W ',
];
const GLINT_LARGE = [
  '  W  ',
  '  W  ',
  'WWRWW',
  '  W  ',
  '  W  ',
];

const GLOW_RED = { 'o': TELEGRAPH };

// Facing → direction index with hysteresis, remembered on the guard (a
// render-only field: the simulation never reads it).
function trackedDir(guard) {
  const dir = quantiseDir(guard.facing.x, guard.facing.y, guard._fxDir);
  guard._fxDir = dir.i;
  return dir;
}

function renderLancerSpear(renderer, guard, side) {
  const dir = trackedDir(guard);
  let thrust = 0;
  let windup = 0;
  if (guard.chargePhase === ChargePhase.WINDUP) {
    windup = 1 - Math.max(0, guard.chargeTimer) / CONST.LANCER_CHARGE_WINDUP;
    thrust = windup < 0.5 ? -1 : -2;
  } else if (guard.chargePhase === ChargePhase.DASH) {
    thrust = 2;
  }
  const g = gridOrigin(guard);
  const hc = side > 0 ? GUARD_COLS - 1 : 0; // gripping hand, sprite cell 'G'
  const hr = 3;
  const spear = spearSprite(dir, thrust);
  spriteAt(renderer, g, hc, hr, spear.rows, SPEAR_PALETTE);

  const tc = hc + spear.tip[0];
  const tr = hr + spear.tip[1];
  if (windup > 0) {
    const flick = 0.75 + 0.25 * Math.sin(fxClock() * 40);
    renderer.setAlpha((0.3 + 0.4 * windup) * flick);
    spriteAt(renderer, g, tc, tr, windup < 0.5 ? GLOW_SMALL : GLOW_LARGE, GLOW_RED);
    renderer.resetAlpha();
    spriteAt(renderer, g, tc, tr, windup < 0.5 ? GLINT_SMALL : GLINT_LARGE, GLINT_PALETTE);
  } else if (guard.chargePhase === ChargePhase.DASH) {
    renderer.setAlpha(0.5);
    spriteAt(renderer, g, tc, tr, GLOW_SMALL, GLOW_RED);
    renderer.resetAlpha();
    cell(renderer, g, tc, tr, '#ffffff');
  }
}

// Charge telegraph: the committed lunge path stepped out on the sprite grid,
// widening to two cells as the windup fills. Tracks the live chargeDir (not
// quantised) — it is the precise promise of where the lunge goes.
function renderChargeTelegraph(renderer, guard) {
  const charge = 1 - Math.max(0, guard.chargeTimer) / CONST.LANCER_CHARGE_WINDUP;
  const reach = (CONST.LANCER_CHARGE_SPEED * CONST.LANCER_CHARGE_DURATION) / GUARD_SCALE;
  const g = gridOrigin(guard);
  const { x: fx, y: fy } = guard.chargeDir;
  const mc = (GUARD_COLS - 1) / 2;
  const mr = (GUARD_ROWS - 1) / 2;
  const wide = charge >= 0.5;
  const [px, py] = Math.abs(fx) > Math.abs(fy) ? [0, 1] : [1, 0];
  renderer.setAlpha(0.18 + 0.32 * charge);
  for (const [c, r] of stepLine(mc, mr, fx, fy, 0, reach)) {
    cell(renderer, g, c, r, TELEGRAPH);
    if (wide) cell(renderer, g, c + px, r + py, TELEGRAPH);
  }
  renderer.resetAlpha();
}

// --- Nocked arrow ---

// The arrow sprite is shared with the one in flight (pixel-art.js), so
// what is nocked and what arrives are the same picture.

// The bow is the sprite's front column ('B'), rows 4–5; the head sits a few
// cells in front of it.
function arrowHand(side) {
  return [side > 0 ? GUARD_COLS - 1 : 0, 4];
}

function renderNockedArrow(renderer, guard, side) {
  const variant = guard.nextArrow;
  if (!variant) return;
  const dir = trackedDir(guard);
  const g = gridOrigin(guard);
  const [hc, hr] = arrowHand(side);
  const headC = hc + Math.round(dir.fx * 4);
  const headR = hr + Math.round(dir.fy * 4);
  // Per-guard phase so a line of archers doesn't flicker in sync (kept ≥ 0:
  // the frame index below is a modulo).
  const time = fxClock() + Math.abs(guard.patrolOffset.y);
  // The glow grows once the shot is nearly off cooldown.
  const ready = 1 - clamp(guard.attackTimer / guard.attackCooldown, 0, 1);
  const glow = ready > 0.6 ? GLOW_LARGE : GLOW_SMALL;

  if (variant === ArrowVariant.FIRE) {
    const flick = 0.8 + 0.2 * Math.sin(time * 23) * Math.sin(time * 7);
    renderer.setAlpha((0.35 + 0.2 * ready) * flick);
    spriteAt(renderer, g, headC, headR, glow, GLOW_FIRE);
    renderer.resetAlpha();
  } else if (variant === ArrowVariant.STICKY) {
    renderer.setAlpha(0.3 + 0.2 * ready);
    spriteAt(renderer, g, headC, headR, glow, GLOW_STICKY);
    renderer.resetAlpha();
  }

  spriteAt(renderer, g, headC, headR, arrowSprite(dir, variant), ARROW_PALETTE);

  if (variant === ArrowVariant.FIRE) {
    const frame = FLAME_FRAMES[Math.floor(time * 10) % FLAME_FRAMES.length];
    spriteAt(renderer, g, headC, headR - 2, frame, FLAME_PALETTE);
    // A spark rises a cell at a time and fades.
    const spark = (time * 1.6) % 1;
    renderer.setAlpha(1 - spark);
    cell(renderer, g, headC + (Math.sin(time * 5) > 0 ? 1 : -1), headR - 4 - Math.floor(spark * 4), '#ffe070');
    renderer.resetAlpha();
  } else if (variant === ArrowVariant.STICKY) {
    // A drop hangs under the glob, then falls a cell at a time and fades.
    const drip = (time * 0.9) % 1;
    const fall = drip < 0.5 ? 0 : Math.floor((drip - 0.5) * 8);
    renderer.setAlpha(drip < 0.5 ? 1 : 1 - (drip - 0.5) * 2);
    cell(renderer, g, headC, headR + 2 + fall, ARROW_PALETTE.G);
    renderer.resetAlpha();
  }
}

export class Guard {
  constructor(x, y, caravan, type = GuardType.BASIC, rng = null, wave = 1, eliteTier = 0) {
    this.pos = new Vec2(x, y);
    this.vel = new Vec2(0, 0);
    this.radius = CONST.GUARD_RADIUS;
    this.caravan = caravan;
    this.type = type;

    // Archetype descriptor — stats, sprites and the two behaviours that
    // differ between guard types. See GUARD_DEFS.
    const def = guardDef(type);
    this.def = def;
    this.maxHp = def.hp;
    this.damage = def.damage;
    this.speed = def.speed;
    this.detectionRange = def.detection;
    this.attackRange = def.attackRange;
    this.attackCooldown = def.attackCooldown;
    this.armor = def.armor;

    // Track wave so attack() can choose elemental projectile variants
    // (e.g. fire arrows past CONST.FIRE_ARROW_MIN_WAVE).
    this.wave = wave;
    // Keep RNG so per-shot decisions (fire vs regular, aim spread) stay
    // seeded for reproducible sim batches.
    this._rng = rng;

    // Wave scaling: +3% HP and +2% damage per wave past wave 5.
    // Early waves stay baseline so the player can bootstrap gold for
    // card purchases. Scaling kicks in mid-game where the card economy
    // provides enough power to match.
    if (wave > 5) {
      const hpScale = 1 + (wave - 5) * 0.03;
      const dmgScale = 1 + (wave - 5) * 0.02;
      this.maxHp = Math.round(this.maxHp * hpScale);
      this.damage = Math.round(this.damage * dmgScale);
      this.armor = Math.round(this.armor * hpScale);
    }

    // Elite tier. Applied on top of wave scaling so an elite is a
    // multiplier on "a guard of this wave", not a fixed late-game stat
    // block. See ELITE_TIERS / planWaveGuards for where the tier comes from.
    this.eliteTier = ELITE_TIERS[eliteTier] ? eliteTier : EliteTier.NONE;
    if (this.eliteTier > 0) {
      const e = ELITE_TIERS[this.eliteTier];
      this.maxHp = Math.round(this.maxHp * e.hp);
      this.damage = Math.round(this.damage * e.dmg);
      this.armor = Math.round(this.armor * e.armor);
      this.speed = this.speed * e.speed;
      // Slightly larger hitbox so the silhouette matches the threat.
      this.radius = CONST.GUARD_RADIUS + this.eliteTier;
    }

    this.hp = this.maxHp;
    this.chaseRange = CONST.GUARD_CHASE_RANGE;
    this.attackTimer = 0;

    // Sticky flag: set true on first fire-arrow shot. Reported in
    // guard_killed so telemetry can distinguish a "burned" archer from
    // one that only ever fired regular arrows (fire is a per-shot dice,
    // not a guard subtype).
    this.hasFiredFire = false;

    // Archer: variant of the arrow that flies next (ArrowVariant), shown on
    // the bow. Rolled on the first update and after every shot — never in
    // render, which the headless simulator doesn't call.
    this.nextArrow = null;

    // Lancer charge state. `chargePhase` non-null means the charge owns
    // movement for this tick and the normal state machine is bypassed.
    this.chargePhase = null;
    this.chargeTimer = 0;
    this.chargeCooldown = 0;
    this.chargeDir = new Vec2(1, 0);
    this._chargeHit = false;

    // Shove from the robber's super strike: while knockTimer > 0 the guard
    // flies along knockVel (decaying) and does nothing else.
    this.knockVel = new Vec2(0, 0);
    this.knockTimer = 0;

    // AI
    this.state = GuardState.PATROL;
    // Set each tick by a guard brain (see guard-brain.js); null keeps the
    // built-in state machine. Shape: { moveTo, attack, charge }.
    this.order = null;
    const rand = randFn(rng);
    this.patrolOffset = new Vec2(
      (rand() - 0.5) * 40,
      (rand() - 0.5) * 40
    );
    this.alive = true;

    // Visual juice
    this.flashTimer = 0;
    // Render-only state with hysteresis (see render / trackedDir): which way
    // the sprite is mirrored, whether the weapon is drawn behind the body,
    // and the weapon's quantised direction. Kept so a facing that wobbles
    // on a threshold doesn't flip the picture every frame. Never read by
    // the simulation.
    this._fxFlip = false;
    this._fxBehind = false;
    this._fxDir = null;

    // Animation - set sprites based on type
    this.animTimer = 0;
    this.animFrame = 0;
    this.facing = new Vec2(1, 0);

    this.walkFrames = def.walk;
    this.idleSprite = def.idle;
    // Elite recolour of the archetype's palette. Resolved once here so
    // render() passes a stable object to the sprite raster cache.
    this.palette = elitePalette(def.palette, this.eliteTier);
  }

  update(dt, playerPos) {
    if (!this.alive) return;

    // Flash timer
    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
    }

    // Attack cooldown
    if (this.attackTimer > 0) {
      this.attackTimer -= dt;
    }
    if (this.chargeCooldown > 0) this.chargeCooldown -= dt;
    if (this.def.attack === archerAttack && this.nextArrow === null) {
      this.nextArrow = rollArrowVariant(this);
    }

    // Staggered by a shove: no AI, no charge, no swings (canAttack) until
    // it lands.
    if (this.knockTimer > 0) {
      this.knockTimer -= dt;
      this.vel = this.knockVel;
      this.pos = this.pos.add(this.knockVel.mul(dt));
      this._clampToWorld();
      this.knockVel = this.knockVel.mul(Math.exp(-CONST.GUARD_KNOCKBACK_DECAY * dt));
      this.animFrame = -1;
      if (this.knockTimer <= 0) {
        this.knockTimer = 0;
        this.knockVel = new Vec2(0, 0);
        this.vel = new Vec2(0, 0);
      }
      return;
    }

    // A charge in progress owns movement, facing and animation for the
    // whole tick — the patrol/chase/return machine is skipped entirely so
    // a committed lunge can't be steered mid-flight.
    if (this.chargePhase) {
      this._updateCharge(dt, playerPos);
      return;
    }

    // An order from a guard brain replaces the built-in decision of where
    // to go; speed, facing and animation below stay shared.
    const targetPos = this.order
      ? this._orderTarget(playerPos)
      : this._builtinTarget(playerPos);
    if (!targetPos) return;

    const toTarget = targetPos.sub(this.pos);
    const distToTarget = toTarget.len();

    if (distToTarget > 3) {
      const dir = toTarget.normalize();
      this.vel = dir.mul(this.speed);
      this.facing = dir;
    } else {
      this.vel = new Vec2(0, 0);
    }

    // Ranged guards always face the player when chasing
    if (this.def.ranged && (this.order || this.state === GuardState.CHASE)) {
      this.facing = playerPos.sub(this.pos).normalize();
    }

    this.pos = this.pos.add(this.vel.mul(dt));
    this._clampToWorld();

    // Animation
    this.animTimer += dt;
    if (this.vel.lenSq() > 10) {
      this.animFrame = Math.floor(this.animTimer / 0.2) % this.walkFrames.length;
    } else {
      this.animFrame = -1; // idle
    }
  }

  // Built-in escort AI: patrol → chase → return state machine. Returns the
  // point to walk toward, or null when a lancer charge began this tick.
  _builtinTarget(playerPos) {
    const distToPlayer = this.pos.dist(playerPos);
    const caravanPos = this.caravan.alive ? this.caravan.pos : this.pos;
    const distToCaravan = this.pos.dist(caravanPos);

    // State transitions. Once the caravan is gone, guards no longer have
    // anything to protect: lock them into CHASE so they keep pursuing the
    // player instead of drifting back to their (now absent) charge.
    if (!this.caravan.alive) {
      this.state = GuardState.CHASE;
    } else {
      switch (this.state) {
        case GuardState.PATROL:
          if (distToPlayer < this.detectionRange) {
            // Alert all siblings — the whole escort joins the chase.
            // The caravan-distance leash in CHASE prevents them from
            // abandoning the caravan entirely.
            for (const g of this.caravan.guards) {
              if (g.alive && g.state === GuardState.PATROL) {
                g.state = GuardState.CHASE;
              }
            }
          }
          break;
        case GuardState.CHASE:
          if (distToPlayer > this.chaseRange || distToCaravan > this.chaseRange * 1.5) {
            this.state = GuardState.RETURN;
          }
          break;
        case GuardState.RETURN:
          // Only re-engage the player after getting back near the caravan.
          // Without this, the guard oscillates between CHASE and RETURN
          // every tick when the player stands at a middling distance.
          if (distToCaravan < 60 && distToPlayer < this.detectionRange * 0.8) {
            this.state = GuardState.CHASE;
          } else if (distToCaravan < 30) {
            this.state = GuardState.PATROL;
          }
          break;
      }
    }

    // Lancer: commit to a charge instead of walking into swing range.
    // Only from CHASE, only inside the charge band (too close and there's
    // nothing to close; too far and the telegraph is unreadable).
    if (this.state === GuardState.CHASE && this._chargeReady(distToPlayer)) {
      this._beginCharge(playerPos);
      return null;
    }

    // Movement based on state
    let targetPos;
    switch (this.state) {
      case GuardState.PATROL:
        targetPos = caravanPos.add(this.patrolOffset);
        break;
      case GuardState.CHASE: {
        if (this.def.ranged) {
          // Ranged guards keep preferred distance from the player but bias
          // toward the caravan side so they don't wander off.
          const preferred = this.def.preferredDist;
          const toPlayer = playerPos.sub(this.pos);
          const dist = toPlayer.len();
          if (dist < preferred * 0.7) {
            // Too close — back toward caravan, not just away from player.
            const retreatDir = this.caravan.pos.sub(this.pos);
            targetPos = retreatDir.lenSq() > 1
              ? this.pos.add(retreatDir.normalize().mul(preferred))
              : this.pos.sub(toPlayer.normalize().mul(preferred));
          } else if (dist > preferred * 1.3) {
            targetPos = playerPos;
          } else {
            targetPos = this.pos;
          }
        } else {
          // Melee guards approach and stop at the player's surface — still
          // inside attack range (guard.attackRange + PLAYER_RADIUS = ~32)
          // but not clipping into the player body.
          const standoff = CONST.PLAYER_RADIUS;
          const fromPlayer = this.pos.sub(playerPos);
          const d = fromPlayer.len();
          targetPos = d > standoff
            ? playerPos.add(fromPlayer.mul(standoff / d))
            : this.pos;
        }
        break;
      }
      case GuardState.RETURN:
        targetPos = caravanPos;
        break;
    }
    return targetPos;
  }

  // Follow an order from a guard brain (see guard-brain.js). The brain
  // picks where to stand and whether to strike; the lancer charge still
  // only fires inside its band and off cooldown.
  _orderTarget(playerPos) {
    const order = this.order;
    if (order.charge !== false && this._chargeReady(this.pos.dist(playerPos))) {
      this._beginCharge(playerPos);
      return null;
    }
    return order.moveTo || this.pos;
  }

  // Lancer charge band + cooldown — mechanics, not tactics, so a brain
  // can veto a charge but never start one outside these limits.
  _chargeReady(distToPlayer) {
    return (
      this.def.charges &&
      this.chargeCooldown <= 0 &&
      distToPlayer > CONST.LANCER_CHARGE_MIN_DIST &&
      distToPlayer < CONST.LANCER_CHARGE_MAX_DIST
    );
  }

  // Clamp to world boundaries so guards (especially archers backing away,
  // or a lancer overshooting a charge) can't leave the playable area.
  _clampToWorld() {
    const world = this.caravan.world;
    this.pos.x = clamp(this.pos.x, this.radius, world.width - this.radius);
    this.pos.y = clamp(this.pos.y, this.radius, world.height - this.radius);
  }

  // Enter the charge windup. Direction is locked when the dash starts, not
  // here, so the lancer tracks the player through the telegraph and only
  // commits at the last moment.
  _beginCharge(playerPos) {
    this.chargePhase = ChargePhase.WINDUP;
    this.chargeTimer = CONST.LANCER_CHARGE_WINDUP;
    this._chargeHit = false;
    this.vel.set(0, 0);
    const to = playerPos.sub(this.pos);
    if (to.lenSq() > 1) {
      this.chargeDir = to.normalize();
      this.facing = this.chargeDir.copy();
    }
  }

  _updateCharge(dt, playerPos) {
    this.chargeTimer -= dt;

    if (this.chargePhase === ChargePhase.WINDUP) {
      // Planted, tracking. The player can read the facing and step aside.
      // A guard brain may aim the lunge elsewhere (order.chargeAim, e.g. where
      // the player will be); speed, duration and damage are unchanged.
      this.vel.set(0, 0);
      this.animFrame = -1;
      const aim = (this.order && this.order.chargeAim) || playerPos;
      const to = aim.sub(this.pos);
      if (to.lenSq() > 1) {
        this.chargeDir = to.normalize();
        this.facing = this.chargeDir.copy();
      }
      if (this.chargeTimer <= 0) {
        this.chargePhase = ChargePhase.DASH;
        this.chargeTimer = CONST.LANCER_CHARGE_DURATION;
        this._chargeHit = false;
      }
      return;
    }

    if (this.chargePhase === ChargePhase.DASH) {
      this.vel = this.chargeDir.mul(CONST.LANCER_CHARGE_SPEED);
      this.pos = this.pos.add(this.vel.mul(dt));
      this._clampToWorld();
      this.animTimer += dt;
      this.animFrame = Math.floor(this.animTimer / 0.08) % this.walkFrames.length;
      if (this.chargeTimer <= 0) {
        this.chargePhase = ChargePhase.RECOVER;
        this.chargeTimer = CONST.LANCER_CHARGE_RECOVER;
        this.vel.set(0, 0);
      }
      return;
    }

    // RECOVER: rooted and defenceless — the reward for dodging the lunge.
    this.vel.set(0, 0);
    this.animFrame = -1;
    if (this.chargeTimer <= 0) {
      this.chargePhase = null;
      this.chargeTimer = 0;
      this.chargeCooldown = CONST.LANCER_CHARGE_COOLDOWN;
    }
  }

  // True while the lancer is mid-lunge and hasn't connected yet.
  get isCharging() {
    return this.chargePhase === ChargePhase.DASH && !this._chargeHit;
  }

  // Shove the guard along (dirX, dirY) at `force` px/s (super strike). A
  // lancer mid-charge loses the charge and goes on cooldown.
  applyKnockback(dirX, dirY, force) {
    if (!this.alive || force <= 0) return;
    const len = Math.sqrt(dirX * dirX + dirY * dirY);
    const nx = len > 0.0001 ? dirX / len : 1;
    const ny = len > 0.0001 ? dirY / len : 0;
    this.knockVel = new Vec2(nx * force, ny * force);
    this.knockTimer = CONST.GUARD_KNOCKBACK_DURATION;
    if (this.chargePhase) {
      this.chargePhase = null;
      this.chargeTimer = 0;
      this.chargeCooldown = CONST.LANCER_CHARGE_COOLDOWN;
    }
  }

  canAttack(playerPos) {
    if (!this.alive) return false;
    if (this.knockTimer > 0) return false;
    // A charge connects on contact, once, independent of the swing timer.
    if (this.isCharging) {
      return this.pos.dist(playerPos) < this.radius + CONST.PLAYER_RADIUS + 4;
    }
    // No ordinary swings while winding up or recovering.
    if (this.chargePhase) return false;
    if (this.order && this.order.attack === false) return false;
    if (this.attackTimer > 0) return false;
    const dist = this.pos.dist(playerPos);
    if (this.def.ranged) {
      return dist < this.attackRange && (this.order || this.state === GuardState.CHASE);
    }
    return dist < this.attackRange + CONST.PLAYER_RADIUS;
  }

  attack(playerPos, playerVel = null) {
    // Charge impact: a melee hit, so i-frames still negate it — the
    // counterplay is timing the dash, not holding it.
    if (this.isCharging) {
      this._chargeHit = true;
      this.attackTimer = this.attackCooldown;
      return {
        ranged: false,
        charge: true,
        damage: Math.round(this.damage * CONST.LANCER_CHARGE_DAMAGE_MUL),
        knockback: {
          x: this.chargeDir.x,
          y: this.chargeDir.y,
          force: CONST.LANCER_CHARGE_KNOCKBACK,
        },
      };
    }

    this.attackTimer = this.attackCooldown;
    // Everything else is archetype-specific — see GUARD_DEFS.
    return this.def.attack(this, playerPos, playerVel);
  }

  takeDamage(amount) {
    if (!this.alive) return;
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
    }
  }

  render(renderer) {
    if (!this.alive) return;

    // Mirror when facing left — switching only once the facing is clearly
    // past the line, so a guard tracking a target straight above or below
    // doesn't flip back and forth.
    if (this.facing.x < -0.18) this._fxFlip = true;
    else if (this.facing.x > -0.02) this._fxFlip = false;
    const flipH = this._fxFlip;
    let sprite;
    if (this.animFrame >= 0) {
      sprite = this.walkFrames[this.animFrame];
    } else {
      sprite = this.idleSprite;
    }

    const drawSprite = flipH ? flipSprite(sprite) : sprite;

    // Charge telegraph, drawn under the sprite: a line along the committed
    // lunge path that fills up over the windup. This is the whole contract
    // with the player — the lunge is unavoidable-looking but always
    // announced, so dodging it is a reaction test, not a coin flip.
    if (this.chargePhase === ChargePhase.WINDUP) renderChargeTelegraph(renderer, this);

    // Weapon overlay (spear / nocked arrow). Behind the body when the guard
    // faces up-screen, in front otherwise.
    const side = flipH ? -1 : 1;
    const overlay = this.def.charges ? renderLancerSpear
      : this.def.attack === archerAttack ? renderNockedArrow
        : null;
    if (this.facing.y < -0.58) this._fxBehind = true;
    else if (this.facing.y > -0.42) this._fxBehind = false;
    const overlayBehind = this._fxBehind;
    if (overlay && overlayBehind) overlay(renderer, this, side);

    // Flash white on damage
    if (this.flashTimer > 0) {
      renderer.pixelSprite(this.pos.x, this.pos.y, drawSprite, whitePalette(this.palette), GUARD_SCALE);
    } else {
      renderer.pixelSprite(this.pos.x, this.pos.y, drawSprite, this.palette, GUARD_SCALE);
    }
    if (overlay && !overlayBehind) overlay(renderer, this, side);

    // Elite marker: a small chevron in the tier colour above the head. The
    // palette recolour alone is easy to miss in a crowd; the pip reads at a
    // glance and is type-independent.
    if (this.eliteTier > 0) {
      const mark = ELITE_TIERS[this.eliteTier].mark;
      const y = this.pos.y - 15;
      for (let i = 0; i < this.eliteTier; i++) {
        renderer.rect(this.pos.x - 3, y - i * 3, 6, 2, mark);
      }
    }

    // Health bar when damaged
    if (this.hp < this.maxHp) {
      const barW = 20;
      const barH = 3;
      const barColor = this.def.barColor;
      renderer.healthBar(
        this.pos.x - barW / 2,
        this.pos.y - 14,
        barW, barH,
        this.hp / this.maxHp,
        barColor,
        CONST.COLOR_HP_BG
      );
    }
  }
}


// Uncapped per-caravan escort size: the def's base count plus wave growth.
export function rawGuardCount(def, wave) {
  return def.guardCount + Math.floor(wave / 3);
}

// Decide how many guards each caravan actually fields, and how hard the
// wave was compressed to get there.
//
// Left alone, the raw formula (per-caravan growth × a caravan count that
// also grows) reaches ~630 guards by wave 70. That is a frame-rate bill,
// not difficulty: a column that deep never reaches the player, so the
// marginal guard adds lag and nothing else. We cap the head count at
// CONST.WAVE_GUARD_CAP and report `pressure` — how many raw guards each
// spawned one now stands in for — which spawnGuards spends on elite tiers.
// Head count per wave (1.8 rebalance, ~/dev/corovans-ai evo/tune.js
// --knob total, smoothed: degree-4 fit of log(total) from wave 5; by hand,
// waves 9–16 nudged up and 23–25 eased into the cap): fitted wave by wave
// so a human-like bot (`human-1`, with the super strike and the horse) vs
// the v4 guard tactics follows the release survival target
// (docs/release-rebalance.md). Boss
// waves included — a boss wave is as hard as its neighbours. Waves 1–4 are
// the 1.7 counts. From wave 26 the WAVE_GUARD_TOTAL_MAX cap holds (past
// the table: ×1.35 a wave, capped). Before it the 1.8 super pass ramped
// waves 15+ to ×2.5 the 1.7 counts by wave 20, which (for human-1) made
// waves 6–15 too easy and 16–24 a climb to 52% death risk on a wave.
export const WAVE_GUARD_TOTALS = [1, 2, 2, 3, 6.37, 7.48, 8.46, 9.29, 10.24, 11.02, 11.53, 11.96, 12.76, 14.34, 15.83, 17.36, 20.01, 24.24, 30.58, 40.3, 55.68, 80.93, 100, 118, 138, 150];
export const WAVE_GUARD_TOTAL_MAX = 150;

export function waveGuardTotal(wave) {
  const n = WAVE_GUARD_TOTALS.length;
  const t = wave <= n ? WAVE_GUARD_TOTALS[Math.max(1, wave) - 1] : WAVE_GUARD_TOTALS[n - 1] * 1.35 ** (wave - n);
  return Math.min(WAVE_GUARD_TOTAL_MAX, t);
}

// Split a wave's head count over its escorts in proportion to `weights`,
// each ≥ 1, summing to exactly round(total) (largest remainder, ties to the
// earlier escort). Rounding each escort on its own moved whole waves in
// steps: four escorts of 2 at a table value of 10 all became 3 — 12 guards.
export function apportionGuards(weights, total) {
  const n = weights.length;
  if (n === 0) return [];
  const want = Math.max(n, Math.round(total));
  const sum = weights.reduce((a, b) => a + b, 0);
  const exact = weights.map(w => (sum > 0 ? w / sum : 1 / n) * want);
  const counts = exact.map(x => Math.max(1, Math.floor(x)));
  let left = want - counts.reduce((a, b) => a + b, 0);
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; left > 0; k = (k + 1) % n, left--) counts[order[k].i]++;
  // The 1-per-escort floor can overshoot: take back from the largest.
  while (left < 0) {
    let j = 0;
    for (let i = 1; i < n; i++) if (counts[i] > counts[j]) j = i;
    counts[j]--;
    left++;
  }
  return counts;
}

export function planWaveGuards(wave, caravans) {
  const raw = caravans.map(c => rawGuardCount(c.def, wave));
  const rawTotal = raw.reduce((a, b) => a + b, 0);
  const cap = CONST.WAVE_GUARD_CAP;

  let counts = raw;
  if (rawTotal > cap) {
    const factor = cap / rawTotal;
    // Never empty an escort completely — a caravan with no guards is free
    // loot, which would make the cap a *reward* for surviving to late waves.
    counts = raw.map(n => Math.max(1, Math.round(n * factor)));
  }

  const capped = counts.reduce((a, b) => a + b, 0);
  const compression = capped > 0 ? rawTotal / capped : 1;
  // Then the escorts are scaled to the wave's head count; elite pressure
  // keeps coming from the cap, as before.
  counts = apportionGuards(counts, waveGuardTotal(wave));
  const total = counts.reduce((a, b) => a + b, 0);
  return {
    counts,
    total,
    rawTotal,
    compression,
    pressure: elitePressure(wave, compression),
  };
}

// How hard this wave leans on elite tiers. Two independent sources, and
// whichever demands more wins:
//
//   - compression: the head-count cap threw bodies away, so the survivors
//     have to stand in for them. Only kicks in once the cap bites (~w45).
//   - wave age: elites are late-game content, but gating them behind the
//     cap alone meant most runs never saw one. From CONST.ELITE_MIN_WAVE
//     they ramp in on their own, gently enough that the debut is a couple
//     of veterans rather than a difficulty step.
//
// Returns a value on the same scale eliteOdds() expects: 1 means "no
// elites", and each whole step above unlocks the next tier.
export function elitePressure(wave, compression = 1) {
  const byWave = wave >= CONST.ELITE_MIN_WAVE
    ? 1 + CONST.ELITE_WAVE_DEBUT + (wave - CONST.ELITE_MIN_WAVE) * CONST.ELITE_WAVE_RAMP
    : 1;
  return Math.max(byWave, compression);
}

// Elite-tier probabilities for a given spawn pressure. At pressure 1 (no
// compression, i.e. every wave below the cap) all three are zero and the
// game behaves exactly as before. Each further whole step of pressure
// unlocks the next tier, so elites ramp in gradually from the wave the cap
// first bites rather than appearing all at once.
export function eliteOdds(pressure) {
  const t = Math.max(0, pressure - 1);
  return {
    veteran: Math.min(0.65, t * 0.35),
    champion: Math.min(0.35, Math.max(0, t - 1) * 0.25),
    executioner: Math.min(0.20, Math.max(0, t - 2.5) * 0.15),
  };
}

// Pick a guard type from a single [0,1) roll.
//
// - Armored from wave 4+. First two armored waves (4–5) at a lower 20%
//   intro rate so the first cliff (telemetry-confirmed wave-4 mortality
//   spike) doesn't shove 2-3 tanks at a player who has no pierce/damage
//   card yet. Wave 6+ resumes the 35% baseline, then past wave 7 grows
//   +3%/wave up to a 65% cap.
// - Archers from wave ARCHER_MIN_WAVE (6) at a fixed 25%. Adding archers on top of wave-6
//   armored density was already stalling runs; keep flat.
// - Lancers from wave 9, bombardiers from wave 11 (1.7 rebalance; were 12 / 16).
//
// Once the shares would sum past 1 (deep late game) they're normalised
// proportionally, which preserves their relative intent and squeezes out
// plain basics rather than silently clipping whichever type is tested last.
export function rollGuardType(wave, roll) {
  if (wave < 4) return GuardType.BASIC;

  const armored = wave < 6
    ? 0.20
    : Math.min(0.65, 0.35 + Math.max(0, wave - 7) * 0.03);
  const archer = wave >= CONST.ARCHER_MIN_WAVE ? 0.25 : 0;
  const lancer = wave >= CONST.LANCER_MIN_WAVE
    ? Math.min(0.20, CONST.LANCER_SHARE_START + (wave - CONST.LANCER_MIN_WAVE) * 0.015)
    : 0;
  const bombardier = wave >= CONST.BOMBARDIER_MIN_WAVE
    ? Math.min(0.12, CONST.BOMBARDIER_SHARE_START + (wave - CONST.BOMBARDIER_MIN_WAVE) * 0.01)
    : 0;

  const sum = armored + archer + lancer + bombardier;
  const norm = sum > 1 ? 1 / sum : 1;

  let acc = armored * norm;
  if (roll < acc) return GuardType.ARMORED;
  acc += archer * norm;
  if (roll < acc) return GuardType.ARCHER;
  acc += lancer * norm;
  if (roll < acc) return GuardType.LANCER;
  acc += bombardier * norm;
  if (roll < acc) return GuardType.BOMBARDIER;
  return GuardType.BASIC;
}

export class Caravan {
  constructor(type, world, rng = null) {
    const def = CARAVAN_DEFS[type];
    this.type = type;
    this.def = def;
    this.world = world;
    this.radius = def.radius;

    // Path following
    this.pathT = 0; // 0 to 1 along the road
    this.speed = def.speed;
    this.direction = 1; // 1 = forward, -1 = reverse (for variety)

    // Position from path
    const startPos = world.getRoadPosition(this.pathT);
    this.pos = new Vec2(startPos.x, startPos.y);

    // Health
    this.maxHp = def.hp;
    this.hp = this.maxHp;
    this.alive = true;

    // Loot (seeded when called from the sim so runs reproduce exactly)
    const rand = randFn(rng);
    this.lootValue = Math.floor(def.lootMin + rand() * (def.lootMax - def.lootMin + 1));

    // Guards
    this.guards = [];

    // State
    this.looted = false; // has been destroyed and loot dropped
    this.escaped = false; // reached end of road without being robbed
    this.isBoss = false; // set to true for boss caravans

    // Visual juice
    this.flashTimer = 0;
  }

  // `opts.count` overrides the raw per-caravan head count (set by
  // planWaveGuards when the wave budget is capped) and `opts.pressure` is
  // how much was compressed away — it drives the elite roll. Called with
  // no opts, behaviour is the pre-cap default: raw count, no elites.
  spawnGuards(wave = 1, rng = null, opts = {}) {
    const count = opts.count != null
      ? Math.max(0, Math.floor(opts.count))
      : rawGuardCount(this.def, wave);
    const odds = eliteOdds(opts.pressure != null ? opts.pressure : 1);
    const rand = randFn(rng);

    // Guards form a column along the road direction behind the caravan,
    // like a real escort marching in file.
    const roadDir = this.world.getRoadDirection(this.pathT);
    // Perpendicular for side offset (slight stagger so they don't stack)
    const perpX = -roadDir.y;
    const perpY = roadDir.x;

    for (let i = 0; i < count; i++) {
      // Place guards behind the caravan along the road, spaced 25px apart
      const behindDist = 30 + i * 25;
      const side = (i % 2 === 0 ? -1 : 1) * 8; // slight left/right stagger
      const gx = this.pos.x - roadDir.x * behindDist + perpX * side;
      const gy = this.pos.y - roadDir.y * behindDist + perpY * side;

      // opts.polygon = { type, share } (sim experiments only): that share of
      // the escort becomes `type`; the rest keeps the normal type roll. Same
      // single roll either way, so the rng stream is unchanged when it's off.
      // opts.lancerShare is the older spelling of { type: lancer, share }.
      const typeRoll = rand();
      const poly = opts.polygon || (opts.lancerShare ? { type: GuardType.LANCER, share: opts.lancerShare } : null);
      const share = poly ? poly.share : 0;
      const guardType = share > 0
        ? (typeRoll < share ? poly.type : rollGuardType(wave, (typeRoll - share) / (1 - share)))
        : rollGuardType(wave, typeRoll);
      // Elite tier is an independent roll: nested thresholds, richest tier
      // tested first (executioner ⊂ champion ⊂ veteran).
      const eliteRoll = rand();
      let tier = EliteTier.NONE;
      if (eliteRoll < odds.executioner) tier = EliteTier.EXECUTIONER;
      else if (eliteRoll < odds.champion) tier = EliteTier.CHAMPION;
      else if (eliteRoll < odds.veteran) tier = EliteTier.VETERAN;

      const guard = new Guard(gx, gy, this, guardType, rng, wave, tier);
      if (opts.heat) guard.speed = heatGuardSpeed(guard.speed, opts.heat);
      // Override random patrol offset with a column formation offset
      // so guards march in file behind the caravan.
      guard.patrolOffset = new Vec2(
        -roadDir.x * behindDist + perpX * side,
        -roadDir.y * behindDist + perpY * side
      );
      this.guards.push(guard);
    }
    return this.guards;
  }

  update(dt, playerPos) {
    if (!this.alive) return;

    if (this.flashTimer > 0) this.flashTimer -= dt;

    // Advance along road
    // Convert speed to path parameter change
    // Road length is roughly world width, so speed / worldWidth gives approximate dt for pathT
    const roadLen = this.world.width * 1.2; // approximate road length (a bit longer than world width due to curves)
    const pathSpeed = this.speed / roadLen;
    this.pathT += pathSpeed * dt * this.direction;

    // If reached end, caravan escapes
    if (this.pathT > 1 || this.pathT < 0) {
      this.alive = false;
      this.looted = true; // mark as done (escaped, no loot)
      this.escaped = true;
      return;
    }

    // Update position from path
    const roadPos = this.world.getRoadPosition(this.pathT);
    this.pos.x = roadPos.x;
    this.pos.y = roadPos.y;
  }

  takeDamage(amount) {
    if (!this.alive) return;
    this.hp -= amount;
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
    }
  }

  render(renderer) {
    if (!this.alive) return;

    let sprite, palette;
    switch (this.type) {
      case CaravanType.DONKEY:
        sprite = DONKEY_SPRITE;
        palette = DONKEY_PALETTE;
        break;
      case CaravanType.WAGON:
        sprite = WAGON_SPRITE;
        palette = WAGON_PALETTE;
        break;
      case CaravanType.ROYAL:
        sprite = ROYAL_SPRITE;
        palette = ROYAL_PALETTE;
        break;
    }

    // Flip based on direction
    const flipH = this.direction < 0;
    const drawSprite = flipH ? flipSprite(sprite) : sprite;
    const spriteScale = this.isBoss ? 3 : 2;

    // Flash white on damage
    if (this.flashTimer > 0) {
      renderer.pixelSprite(this.pos.x, this.pos.y, drawSprite, whitePalette(palette), spriteScale);
    } else {
      renderer.pixelSprite(this.pos.x, this.pos.y, drawSprite, palette, spriteScale);
    }

    // Boss indicator
    if (this.isBoss) {
      renderer.circle(this.pos.x, this.pos.y - this.radius - 12, 5, '#ffd700');
      renderer.circle(this.pos.x, this.pos.y - this.radius - 12, 3, '#ffee44');
    }

    // Health bar when damaged
    if (this.hp < this.maxHp) {
      const barW = this.isBoss ? 50 : 30;
      const barH = this.isBoss ? 5 : 4;
      renderer.healthBar(
        this.pos.x - barW / 2,
        this.pos.y - this.radius - 6,
        barW, barH,
        this.hp / this.maxHp,
        this.isBoss ? '#ff4444' : '#e74c3c',
        CONST.COLOR_HP_BG
      );
    }
  }
}


// Push overlapping guards apart and out of their caravans. Runs after the
// guard AI has moved everyone for the frame. Single pass is enough for the
// cluster sizes we deal with (a few guards per caravan).
export function resolveGuardCollisions(guards, caravans) {
  // Filter once instead of re-testing `alive` inside the O(n²) inner loop —
  // by late waves most of the array is corpses that stay until the wave ends.
  const live = [];
  for (const g of guards) if (g.alive) live.push(g);

  // Guard vs guard
  for (let i = 0; i < live.length; i++) {
    const a = live[i];
    for (let j = i + 1; j < live.length; j++) {
      const b = live[j];
      const dx = b.pos.x - a.pos.x;
      const dy = b.pos.y - a.pos.y;
      const distSq = dx * dx + dy * dy;
      const minDist = a.radius + b.radius;
      if (distSq >= minDist * minDist) continue;
      if (distSq > 0.0001) {
        const dist = Math.sqrt(distSq);
        const push = (minDist - dist) / 2;
        const nx = dx / dist;
        const ny = dy / dist;
        a.pos.x -= nx * push;
        a.pos.y -= ny * push;
        b.pos.x += nx * push;
        b.pos.y += ny * push;
      } else {
        // Exact overlap — nudge one arbitrarily so the next pass can resolve.
        b.pos.x += minDist;
      }
    }
  }

  // Guard vs caravan — the caravan follows its road, so only the guard moves.
  for (const c of caravans) {
    if (!c.alive) continue;
    for (const g of live) {
      const dx = g.pos.x - c.pos.x;
      const dy = g.pos.y - c.pos.y;
      const distSq = dx * dx + dy * dy;
      const minDist = g.radius + c.radius;
      if (distSq >= minDist * minDist) continue;
      if (distSq > 0.0001) {
        const dist = Math.sqrt(distSq);
        const overlap = minDist - dist;
        g.pos.x += (dx / dist) * overlap;
        g.pos.y += (dy / dist) * overlap;
      } else {
        g.pos.x += minDist;
      }
    }
  }
}

// Average speed of a robber with no cards who dashes on every cooldown.
export function dashPace() {
  const cd = CONST.PLAYER_DASH_COOLDOWN;
  const dash = CONST.PLAYER_DASH_DURATION;
  return (CONST.PLAYER_SPEED * (cd - dash) + CONST.PLAYER_DASH_SPEED * dash) / cd;
}

// Guard speed at a heat level (rebirths so far in this run). See
// CONST.HEAT_GUARD_SPEED_STEP; never slows a guard down.
export function heatGuardSpeed(speed, heat) {
  if (!(heat > 0)) return speed;
  const cap = CONST.HEAT_GUARD_SPEED_CAP * dashPace();
  return Math.max(speed, Math.min(speed * (1 + CONST.HEAT_GUARD_SPEED_STEP * heat), cap));
}

// Wave spawning system
export function spawnWave(wave, world, rng = null) {
  const caravans = [];
  const isBossWave = wave > 0 && wave % 5 === 0;
  const rand = randFn(rng);

  // Scaling: more caravans as waves progress. Grows slower past wave 12
  // so mid-game doesn't spike too hard, but never caps — late waves keep
  // adding pressure.
  let caravanCount;
  if (wave <= 5) {
    caravanCount = 1 + Math.floor(wave / 2);        // 1,2,2,3,3
  } else if (wave <= 12) {
    caravanCount = Math.floor(wave / 2);             // 3..6
  } else {
    caravanCount = 6 + Math.floor((wave - 12) / 3);  // 6,7,7,7,8,8,8,9...
  }

  if (isBossWave) {
    // Boss wave: spawn one boss caravan + fewer regular caravans.
    // Boss HP scales with wave number: base ×3 at wave 5, growing +1× per
    // boss cycle (×4 at wave 10, ×5 at wave 15, etc.) so late bosses are
    // real walls, not speed bumps.
    const bossCaravan = new Caravan(CaravanType.ROYAL, world, rng);
    bossCaravan.isBoss = true;
    const bossHpMul = CONST.BOSS_HP_MULTIPLIER + Math.floor(wave / 5) - 1;
    bossCaravan.maxHp = Math.round(bossCaravan.maxHp * bossHpMul);
    bossCaravan.hp = bossCaravan.maxHp;
    const bossLootMul = CONST.BOSS_LOOT_MULTIPLIER_BASE + Math.floor(wave / 5) - 1;
    bossCaravan.lootValue = Math.round(bossCaravan.lootValue * bossLootMul);
    bossCaravan.radius = 28; // bigger sprite

    // Override guard count for boss caravan
    bossCaravan.def = { ...bossCaravan.def, guardCount: CONST.BOSS_GUARD_COUNT };
    bossCaravan.pathT = 0.02;
    bossCaravan.direction = 1;
    const roadPos = world.getRoadPosition(bossCaravan.pathT);
    bossCaravan.pos.x = roadPos.x;
    bossCaravan.pos.y = roadPos.y;
    caravans.push(bossCaravan);

    // Fewer regular caravans on boss waves
    caravanCount = Math.max(1, Math.floor(caravanCount / 2));
  }

  const startIdx = caravans.length;
  // Tight chain: caravans form a dense column like a real caravan train.
  // ~0.12 pathT span keeps the whole group within ~240px on the road.
  const totalSlots = startIdx + caravanCount;
  const spacing = totalSlots > 1 ? 0.12 / (totalSlots - 1) : 0;

  for (let i = 0; i < caravanCount; i++) {
    // Choose type based on wave
    let type;
    if (wave <= 2) {
      type = CaravanType.DONKEY;
    } else if (wave <= 4) {
      type = rand() < 0.6 ? CaravanType.DONKEY : CaravanType.WAGON;
    } else {
      const roll = rand();
      if (roll < 0.3) type = CaravanType.DONKEY;
      else if (roll < 0.7) type = CaravanType.WAGON;
      else type = CaravanType.ROYAL;
    }

    const caravan = new Caravan(type, world, rng);

    // Stagger starting positions along the left portion of the road
    caravan.pathT = 0.02 + ((startIdx + i) * spacing);
    caravan.direction = 1;
    const roadPos = world.getRoadPosition(caravan.pathT);
    caravan.pos.x = roadPos.x;
    caravan.pos.y = roadPos.y;

    caravans.push(caravan);
  }

  return caravans;
}
